import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

const DeveloppementMobile = () => {
  const features = [
    "Authentification utilisateur",
    "Notifications push",
    "Géolocalisation",
    "Paiement in-app",
    "Mode hors ligne",
    "Synchronisation cloud",
    "Partage sur réseaux sociaux",
    "Scan QR/Code-barres",
    "Chat en temps réel",
  ];

  const process = [
    { num: "01", title: "Analyse & Conception", desc: "Définition des fonctionnalités et design UX/UI" },
    { num: "02", title: "Développement", desc: "Codage avec méthodologie agile et sprints réguliers" },
    { num: "03", title: "Tests", desc: "Tests fonctionnels, performance et compatibilité" },
    { num: "04", title: "Déploiement", desc: "Publication sur App Store et Google Play" },
    { num: "05", title: "Maintenance", desc: "Support, mises à jour et évolutions" },
  ];

  const projects = [
    {
      title: "Contrôle Routier",
      description: "Application mobile professionnelle pour la gestion des contrôles routiers",
      image: "/images/dev-mobile/654133.png",
    },
    {
      title: "Team Shuffle",
      description: "Application pour créer des équipes de football équilibrées",
      image: "/images/dev-mobile/miniature.jpeg",
      link: "https://teamshuffle.flowkraftagency.com/",
    },
  ];

  return (
    <div className="bg-gray-950 text-white min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-end pb-20">
        <div className="absolute inset-0">
          <img
            src="/images/dev-mobile/2150104509.jpg"
            alt="Développement Mobile"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/70 to-gray-950/30" />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 w-full">
          <div className="max-w-3xl">
            <div className="inline-block px-4 py-1 border border-flowkraft-primary/30 text-flowkraft-secondary text-sm mb-6">
              Services
            </div>
            <h1 className="font-display text-display text-white mb-6">
              Développement Mobile
            </h1>
            <p className="text-xl text-gray-300 max-w-xl">
              Applications natives et cross-platform pour iOS et Android
            </p>
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="py-20 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-display text-title text-white mb-6">
                Applications Mobiles Sur Mesure
              </h2>
              <p className="text-gray-400 text-lg leading-relaxed mb-6">
                Nous développons des applications mobiles performantes et intuitives
                qui offrent une expérience utilisateur exceptionnelle sur iOS et
                Android.
              </p>
              <p className="text-gray-400 text-lg leading-relaxed">
                Notre expertise couvre le développement natif et cross-platform,
                nous permettant de choisir la meilleure approche pour votre projet.
              </p>
            </div>
            <div className="relative">
              <img
                src="/images/dev-mobile/74125.jpeg"
                alt="Mobile Development"
                className="w-full aspect-[4/3] object-cover"
              />
              <div className="absolute inset-0 border border-white/10" />
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-display text-title text-white mb-16">
            Fonctionnalités Clés
          </h2>

          <div className="grid md:grid-cols-3 gap-4">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex items-center border border-white/10 p-5 hover:border-flowkraft-primary/50 transition-colors"
              >
                <Check className="w-5 h-5 text-flowkraft-secondary mr-4 flex-shrink-0" />
                <span className="text-gray-300">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-gray-900/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-display text-title text-white mb-16">
            Notre Processus
          </h2>

          <div className="grid md:grid-cols-5 gap-px bg-white/10">
            {process.map((step, index) => (
              <div key={index} className="bg-gray-950 p-6 text-center">
                <div className="font-display text-4xl text-flowkraft-primary/30 mb-4">
                  {step.num}
                </div>
                <h3 className="text-white font-medium mb-2">{step.title}</h3>
                <p className="text-gray-500 text-sm">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-display text-title text-white mb-16">
            Nos Applications Mobiles
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <div key={index} className="group relative overflow-hidden aspect-video">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/50 to-transparent" />
                <div className="absolute inset-0 p-8 flex flex-col justify-end">
                  <h3 className="font-display text-2xl text-white mb-2">
                    {project.title}
                  </h3>
                  <p className="text-gray-400 mb-4">
                    {project.description}
                  </p>
                  {project.link && (
                    <a
                      href={project.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-flowkraft-secondary font-medium"
                    >
                      Voir le projet
                      <ArrowUpRight className="w-4 h-4 ml-1" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 relative">
        <div className="absolute inset-0 opacity-10">
          <img
            src="/images/main/cta_background_digital.png"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 text-center">
          <h2 className="font-display text-title text-white mb-6">
            Transformez votre idée en application mobile
          </h2>
          <p className="text-gray-400 text-lg mb-8 max-w-xl mx-auto">
            Discutons de votre projet d'application mobile
          </p>
          <Link
            to="/#contact"
            className="btn-solid inline-flex"
          >
            Demander un devis
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DeveloppementMobile;
